import { useEffect, useState, useMemo, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DASHBOARD_TUTORIAL_VERSION } from "@/components/Tutorial";

import {
  Flame,
  Type,
  ListChecks,
  ChevronRight,
  RefreshCw,
  Sparkles,
  Languages,
  BookOpen,
} from "lucide-react";
import { t } from "@/i18n";

// 1. On stocke la promesse d'import UNE SEULE FOIS pour éviter les erreurs du bundler
const islandWidgetPromise = import("./dashboard/IslandWidget");

// 2. On passe la promesse globale à lazy()
const IslandWidget = lazy(() => islandWidgetPromise);
const Tutorial = lazy(() => import("@/components/Tutorial"));

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();

  // On ne bloque la page que pour le summary
  const [sLoading, setSLoading] = useState(true);

  const [s, setS] = useState(null);
  const [d, setD] = useState(null);
  const [vocabStats, setVocabStats] = useState({ mastered: 0, total: 0 });

  const [showTuto, setShowTuto] = useState(false);

  // Préchargement du chunk et lancement de la requête Oasis
  useEffect(() => {
    islandWidgetPromise
      .then((m) => {
        if (m.prefetchOasis) m.prefetchOasis();
      })
      .catch(console.error);
  }, []);

  // Empêche la page de défiler
  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;

    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = prevOverflow;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, []);

  // Fetch des données
  useEffect(() => {
    let isMounted = true;

    // Fetch summary seul pour débloquer l'interface vite
    api
      .get("/progress/summary")
      .then((r) => {
        if (isMounted) {
          setS(r.data);
          setSLoading(false);
        }
      })
      .catch((error) => {
        console.error("Erreur summary", error);
        if (isMounted) setSLoading(false);
      });

    // Fetch detail et vocab en parallèle, sans bloquer la page
    Promise.allSettled([
      api.get("/progress/detail"),
      api.get("/progression/vocab-groups"),
    ]).then(([detailRes, vocabRes]) => {
      if (!isMounted) return;

      let detailData = null;
      if (detailRes.status === "fulfilled") {
        detailData = detailRes.value.data;
        setD(detailData);
      }

      // Calcul du vocabulaire (maîtrisé / total)
      if (vocabRes.status === "fulfilled") {
        const groups = vocabRes.value.data?.groups || [];
        if (groups.length > 0) {
          setVocabStats({
            mastered: groups.reduce((n, g) => n + (g.mastered || 0), 0),
            total: groups.reduce((n, g) => n + (g.total || 0), 0),
          });
        } else {
          setVocabStats({
            mastered: detailData?.words?.mastered || 0,
            total: detailData?.words?.seen || 0,
          });
        }
      } else if (detailData) {
        setVocabStats({
          mastered: detailData.words?.mastered || 0,
          total: detailData.words?.seen || 0,
        });
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      user &&
      (user.tutorial_seen === false ||
        localStorage.getItem("replayTutorial") === "1" ||
        localStorage.getItem("harf_dashboard_tutorial_version") !==
          DASHBOARD_TUTORIAL_VERSION)
    ) {
      setShowTuto(true);
    }
  }, [user]);

  if (sLoading) {
    return (
      <div className="flex flex-col h-full gap-4 p-4 animate-pulse lg:grid lg:grid-cols-[minmax(320px,0.85fr)_minmax(500px,1.5fr)] lg:grid-rows-[auto_1fr] lg:max-w-7xl lg:mx-auto lg:w-full lg:p-6">
        <div className="h-8 w-40 bg-muted rounded-full shrink-0 lg:col-span-2" />
        <div className="flex flex-col gap-4">
          <div className="h-20 bg-muted rounded-2xl w-full shrink-0" />
          <div className="h-20 bg-muted rounded-2xl w-full shrink-0" />
          <div className="h-20 bg-muted rounded-2xl w-full shrink-0" />
          <div className="h-20 bg-muted rounded-2xl w-full shrink-0" />
        </div>
        <div className="min-h-[500px] bg-muted/50 rounded-2xl w-full" />
      </div>
    );
  }

  // Ne bloque qu'en cas d'erreur de summary (détail charge en fond)
  if (!s) {
    return (
      <div className="text-center p-4 text-muted-foreground">
        {t("dashboard.erreur_de_chargement")}
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes pulse-border {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0); }
        }
        .animate-pulse-border {
          animation: pulse-border 2s infinite;
        }
      `}</style>

      <div
        className="
          flex flex-col
          w-full
          max-w-3xl
          mx-auto
          overflow-hidden
          gap-3
          px-3
          pb-2
          sm:px-4
          sm:pb-4
          -mt-4
          sm:-mt-1

          lg:grid
          lg:grid-cols-[minmax(340px,0.82fr)_minmax(520px,1.65fr)]
          lg:grid-rows-[auto_1fr]
          lg:max-w-7xl
          lg:gap-5
          lg:px-6
          lg:pb-6
          lg:overflow-visible
          lg:-mt-1
        "
        style={{
          height: "calc(100dvh - 155px - env(safe-area-inset-bottom))",
          minHeight: 0,
          maxHeight: "calc(100dvh - 64px - 64px - env(safe-area-inset-bottom))",
        }}
      >
        {showTuto && (
          <Suspense fallback={null}>
            <Tutorial onClose={() => setShowTuto(false)} />
          </Suspense>
        )}

        <div className="flex flex-col gap-3 min-h-0 lg:row-span-2 lg:overflow-y-auto lg:pr-1">
          {/* Header */}
          <div className="flex items-center justify-between shrink-0">
            <h1 className="font-head text-xl font-bold flex items-center gap-2 truncate">
              {t("dashboard.bonjour")} {user?.name?.split(" ")[0]} 👋
            </h1>

            <span className="flex items-center gap-1 rounded-full border bg-card px-3 py-1.5 text-sm font-bold shadow-sm">
              <Flame size={18} className="text-ocre" />
              {s.streak}
            </span>
          </div>

          {/* Actions rapides */}
          <div className="flex flex-col gap-2 shrink-0">
            <button
              onClick={() => nav("/practice/alphabet")}
              className="flex items-center justify-between rounded-2xl border-2 border-amber-400 bg-amber-500/10 p-3 transition-transform hover:scale-[1.01] active:scale-95 animate-pulse-border"
            >
              <div className="flex items-center gap-3">
                <div className="bg-amber-500 text-white p-2 rounded-xl shrink-0">
                  <Type size={20} />
                </div>
                <div className="text-left leading-tight">
                  <div className="font-head text-base font-bold text-amber-950 dark:text-amber-50">
                    {t("dashboard.apprendre_l_alphabet")}
                  </div>
                  <div className="hidden lg:block text-xs text-amber-700/70 dark:text-amber-300/70 mt-1">
                    {t("dashboard.commence_par_maitriser_les_bases")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="bg-amber-500/20 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider">
                  {t("dashboard.priorite")}
                </span>
                <ChevronRight size={18} className="text-amber-600/60" />
              </div>
            </button>

            <button
              onClick={() => nav("/vocab-quiz")}
              className="flex items-center justify-between rounded-2xl border border-border bg-card shadow-sm p-3 transition-all hover:bg-secondary hover:scale-[1.01] active:scale-95"
            >
              <div className="flex items-center gap-3">
                <div className="bg-primary text-white p-2 rounded-xl shrink-0">
                  <ListChecks size={20} />
                </div>
                <div className="text-left leading-tight">
                  <div className="font-head text-base font-bold">
                    {t("dashboard.apprendre_des_mots")}
                  </div>
                  <div className="hidden lg:block text-xs text-muted-foreground mt-1">
                    {t("dashboard.enrichis_ton_vocabulaire")}
                  </div>
                </div>
              </div>
              <ChevronRight
                size={18}
                className="text-muted-foreground/50 shrink-0"
              />
            </button>
          </div>

          {/* Sous-groupe : Progrès (Acquis) */}
          <div className="shrink-0 flex flex-col gap-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-1">
              {t("dashboard.deja_acquis")}
            </div>

            <div className="grid grid-cols-3 gap-2 min-h-[95px]">
              {/* Lettres */}
              <button
                onClick={() => nav("/progress")}
                className="rounded-2xl border bg-card p-2 text-center flex flex-col justify-center items-center transition-all hover:border-[#7C3AED]/50 hover:bg-[#7C3AED]/5"
              >
                <Type size={18} className="mb-1 text-[#7C3AED]" />
                {!d ? (
                  <div className="h-5 w-10 bg-muted rounded animate-pulse my-0.5" />
                ) : (
                  <div className="font-head text-lg font-bold leading-none flex items-baseline gap-0.5">
                    {d.letters?.mastered || 0}
                    <span className="text-xs text-muted-foreground font-normal">
                      /{d.letters?.total || 28}
                    </span>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">
                  {t("dashboard.lettres")}
                </div>
              </button>

              {/* Mots */}
              <button
                onClick={() => nav("/progress")}
                className="rounded-2xl border bg-card p-2 text-center flex flex-col justify-center items-center transition-all hover:border-[#0EA5E9]/50 hover:bg-[#0EA5E9]/5"
              >
                <Languages size={18} className="mb-1 text-[#0EA5E9]" />
                {!d ? (
                  <div className="h-5 w-10 bg-muted rounded animate-pulse my-0.5" />
                ) : (
                  <div className="font-head text-lg font-bold leading-none flex items-baseline gap-0.5">
                    {vocabStats.mastered || 0}
                    <span className="text-xs text-muted-foreground font-normal">
                      /{vocabStats.total || 0}
                    </span>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">
                  {t("dashboard.mots")}
                </div>
              </button>

              {/* Leçons */}
              <button
                onClick={() => nav("/progress")}
                className="rounded-2xl border bg-card p-2 text-center flex flex-col justify-center items-center transition-all hover:border-[#16A34A]/50 hover:bg-[#16A34A]/5"
              >
                <BookOpen size={18} className="mb-1 text-[#16A34A]" />
                {!d ? (
                  <div className="h-5 w-10 bg-muted rounded animate-pulse my-0.5" />
                ) : (
                  <div className="font-head text-lg font-bold leading-none flex items-baseline gap-0.5">
                    {d.lessons_done_count || 0}
                    <span className="text-xs text-muted-foreground font-normal">
                      /{d.total_lessons || 0}
                    </span>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">
                  {t("admin.lecons")}
                </div>
              </button>
            </div>
          </div>

          {/* Révisions et erreurs */}
          <div className="grid grid-cols-2 gap-2.5 shrink-0 mt-1">
            <button
              onClick={() => nav("/review")}
              disabled={!s.due_reviews}
              className="flex items-center justify-between rounded-2xl border bg-card p-3 disabled:opacity-50 transition-all hover:bg-secondary hover:scale-[1.02] active:scale-95"
            >
              <div className="flex items-center gap-2 min-w-0">
                <RefreshCw size={18} className="text-primary shrink-0" />
                <span className="text-sm font-bold truncate">
                  {t("dashboard.revisions")}
                </span>
              </div>
              <span className="text-xs font-bold bg-primary/10 text-primary px-2.5 py-1 rounded-lg shrink-0">
                {s.due_reviews || 0}
              </span>
            </button>

            <button
              onClick={() => nav("/my-errors")}
              disabled={s.errors === 0}
              className="flex items-center justify-between rounded-2xl border bg-card p-3 disabled:opacity-50 transition-all hover:bg-secondary hover:scale-[1.02] active:scale-95"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles size={18} className="text-amber-500 shrink-0" />
                <span className="text-sm font-bold truncate">
                  {t("dashboard.erreurs")}
                </span>
              </div>
              <span className="text-xs font-bold bg-amber-500/10 text-amber-600 px-2.5 py-1 rounded-lg shrink-0">
                {s.errors || 0}
              </span>
            </button>
          </div>
        </div>

        <div className="flex-1 relative w-full min-h-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:h-full">
          <div className="absolute inset-0 rounded-2xl border overflow-hidden flex flex-col bg-card shadow-sm lg:shadow-md">
            <Suspense
              fallback={<div className="flex-1 bg-muted/30 animate-pulse" />}
            >
              <IslandWidget />
            </Suspense>
          </div>
        </div>
      </div>
    </>
  );
}
